import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export interface Note {
  id: string
  content: string
  timestamp: Date
  caseId?: string
  category?: string // e.g., 'income', 'assets', 'collateral'
  tags?: string[]
  author?: string
  metadata?: {
    annotationId?: string
    screenshot?: string // base64 image
    position?: { x: number; y: number; w: number; h: number; page: number }
    extractedText?: string
  }
}

interface NotesState {
  entities: Record<string, Note>
  ids: string[]
  byCaseId: Record<string, string[]>
}

const initialState: NotesState = {
  entities: {
  },
  ids: [],
  byCaseId: {}
}

const notesSlice = createSlice({
  name: 'notes',
  initialState,
  reducers: {
    addNote(state, action: PayloadAction<Omit<Note, 'id'>>) {
      const id = Date.now().toString()
      const note: Note = {
        ...action.payload,
        id
      }
      state.entities[id] = note
      state.ids.push(id)
      
      // Add to case index if caseId provided
      if (note.caseId) {
        if (!state.byCaseId[note.caseId]) {
          state.byCaseId[note.caseId] = []
        }
        state.byCaseId[note.caseId].push(id)
      }
    },
    
    updateNote(state, action: PayloadAction<{ id: string; content: string }>) {
      const { id, content } = action.payload
      if (state.entities[id]) {
        state.entities[id].content = content
      }
    },
    
    deleteNote(state, action: PayloadAction<string>) {
      const id = action.payload
      const note = state.entities[id]
      
      if (note) {
        // Remove from entities
        delete state.entities[id]
        
        // Remove from ids array
        state.ids = state.ids.filter(noteId => noteId !== id)
        
        // Remove from case index
        if (note.caseId && state.byCaseId[note.caseId]) {
          state.byCaseId[note.caseId] = state.byCaseId[note.caseId].filter(
            noteId => noteId !== id
          )
        }
      }
    },
    
    clearNotes(state) {
      state.entities = {}
      state.ids = []
      state.byCaseId = {}
    }
  }
})

export const { addNote, updateNote, deleteNote, clearNotes } = notesSlice.actions
export default notesSlice.reducer
